import { APP_BASE_HREF } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Event, NavigationCancel, NavigationEnd, NavigationError, Router, RouterEvent } from '@angular/router';

import { MessageService } from 'primeng/api';
import { Observable, Subscription, catchError } from 'rxjs';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';

import { ConfigService } from '../../../Core/Config/services/config.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MfaModel, MfaStatusTypes, MfaTypesEnum } from '../../../Core/Users/models/mfa.model';

import { LoginApi } from '../../../api/login/login.api';
import { UserStateApiModel } from '../../../api/users/models/user-state.api.model';

import { partnerResellerConfigs } from '../../config/resellers/reseller-config-model';
import { LoginInfo } from '../../models/login-info.model';
import { MfaService } from '../../services/mfa.service';
import { WebsocketAwareNeptuneService } from '../../services/websocket-aware-neptune.service';

@Component({
    selector: 'agpr-mfa-login',
    styleUrls: ['./mfa-login.component.scss'],
    templateUrl: './mfa-login.component.html',
    standalone: false
})
export class MfaLoginComponent implements OnDestroy {
    readonly mfaTypes = MfaTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly logoAltTextMap: Map<string, string> = new Map([
        [ConfigService.burrellesLuceGUID, 'Core.login.burrelles.logoAltText'],
        [ConfigService.lexisNexisGUID, 'Core.login.lexis.logoAltText'],
        [ConfigService.newsDirect, 'Core.login.newsdirect.logoAltText'],
        [ConfigService.paMedia, 'Core.login.paMedia.logoAltText']
    ]);

    subscriptions: Subscription[] = [];

    logoUrl: string;
    logoAltText: string;
    logoRouterLink = undefined;
    loginInfo: LoginInfo = null;
    isResellerDomain: boolean = false;

    // Tracks the status of the agility login, it needs to be completed in different ways depending on what the next step is
    tosOrPassword: boolean = false;
    returnUrl: string;

    setupRequired: boolean = false;
    useDifferentMethod: boolean = false;
    selectedMfaMethod: MfaModel;
    verifiedMfaMethods: MfaModel[] = [];
    username: string;
    isCodeInvalid: boolean;
    loading: boolean = false;

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private websocketAwareNeptuneService: WebsocketAwareNeptuneService,
        private localizationService: LocalizationService,
        private router: Router,
        private route: ActivatedRoute,
        private titleService: Title,
        private configService: ConfigService,
        private mfaService: MfaService,
        private loginApi: LoginApi,
        private messageService: MessageService,
    ) {
        this.logoUrl = appBaseHref + 'assets/images/agility-logo-horizontal.png';
        this.logoAltText = 'Agility PR Solutions';
        this.titleService.setTitle(this.localizationService.transform('Mfa.browserTitle'));
        this.loginInfo = this.mfaService.getLoginInfo();
        this.username = this.loginInfo.username;

        if (this.configService.hasBrandGUID()) {
            this.isResellerDomain = true;
            const resellerConfig = partnerResellerConfigs.get(this.configService.getPartnerReseller());
            this.logoUrl = resellerConfig?.logoUrl;
            this.logoAltText = this.logoAltTextMap.get(this.configService.getBrandGUID());
        }

        this.subscriptions.push(
            this.mfaService.mfaStatus$.subscribe(
                statusModel => {
                    if (statusModel) {
                        this.setupRequired = statusModel.status === MfaStatusTypes.MFA_SETUP_REQUIRED;
                        if (statusModel.status === MfaStatusTypes.MFA_VALIDATION_REQUIRED && statusModel.verifiedMfaMethods) {
                            this.verifiedMfaMethods = statusModel.verifiedMfaMethods.map(method => new MfaModel(method));
                            this.selectedMfaMethod = this.verifiedMfaMethods.find(x => x.preferred);
                        }
                    }

                }
            ),
            this.router.events.subscribe((event: Event | RouterEvent) => {
                if (event instanceof NavigationEnd ||
                    event instanceof NavigationCancel ||
                    event instanceof NavigationError
                ) {
                    this.loading = false;
                }
            }),
            this.route.queryParams.subscribe(
                queryParams => {
                    // Sometimes, the login page gets set as the return URL, we don't want to redirect to login on login, ignore that
                    // TODO: For now, we won't allow any relative URLs, these cause an issue when there are neptune only
                    //  and classic accounts being used at the same time, check for http, which also checks for https
                    if (
                        queryParams.returnUrl &&
                        queryParams.returnUrl.toLowerCase().indexOf('login') === -1 &&
                        queryParams.returnUrl.indexOf('http') !== -1
                    ) {
                        this.returnUrl = queryParams.returnUrl;
                    }
                }
            )
        );
    }

    backToLogin() {
        this.router.navigate(['Login']);
    }

    performLogin() {
        this.login(this.selectedMfaMethod).subscribe();
    }

    // use this login observable to pass as input for MfaSetupComponent
    login(mfaModel: MfaModel): Observable<UserStateApiModel> {
        this.loading = true;
        return this.mfaService.performMfaLogin(mfaModel.toMfaVerifyModel(this.username)).pipe(
            catchError((error) => {
                this.isCodeInvalid = true;
                this.loading = false;
                throw error;
            })
        );
    }

    goToConfirm() {
        if (this.selectedMfaMethod.type === MfaTypesEnum.email) {
            this.loginApi.sendMfaEmailConfirmation(this.selectedMfaMethod.email, this.selectedMfaMethod.id, this.username)
                .subscribe({
                    next: () => {
                        this.useDifferentMethod = false;
                        this.messageService.add({
                            severity: messageSeverityEnum.success,
                            summary: 'Mfa.setup.confirm.success'
                        });
                    },
                    error: () =>  {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Mfa.setup.confirm.error',
                        });
                    }
                });
        } else {
            this.useDifferentMethod = false;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
